/*
 * Slide Templates — the Asset Library's "Slide Templates" tab content.
 *
 * Fetches the template manifest (services/slideTemplatesApi.ts) and renders
 * it as a preview grid, alongside a "Filter by" sidebar built from the
 * manifest's own `category` field (e.g. "Bio", "Chart") — not a separate
 * curated taxonomy, and there's no favourites concept. Category filtering and
 * the client-side name/category/layoutClass search narrow the grid together.
 *
 * THE CARDS
 * The manifest carries no rendered thumbnails, so a card would otherwise be an
 * empty grey box — a hundred-odd identical ones. Instead each card draws the
 * entry's placeholder rectangles (normalised into `template.preview` by the
 * api module) as a role-coloured wireframe, which is a real, distinguishable
 * picture of the layout. A plain placeholder box is only used for entries with
 * no usable geometry at all.
 *
 * An entry is only insertable when its manifest record carries a `pptxFile`
 * (see services/slideTemplatesApi.ts) — most do, but a stale/malformed one
 * won't. Badging every card "Preview only" would be noise on a set that's
 * almost entirely insertable, so the "none insertable" case gets one notice
 * above the grid instead; the per-card badge appears only in a mixed set,
 * where it actually distinguishes one card from another.
 *
 * The "Insert into deck" action lives in the shared AssetLibraryContent
 * footer, not here, so this component exposes an imperative `insertSelected`
 * handle (via ref) plus a selection-state callback so that footer can drive
 * this panel's insert flow and reflect its selection count/button state.
 */

import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useSlideTemplateThumbnail } from "../hooks/useSlideTemplateThumbnail";
import { useSlideTemplates, type UseSlideTemplatesOptions } from "../hooks/useSlideTemplates";
import type { SlidePreview, SlidePreviewRole, SlideTemplate } from "../services/slideTemplatesApi";
import type { ExtractedSlideImage } from "../../shared/services/pptxSlideImageExtraction";
import styles from "./SlideTemplatesPanel.module.css";

export interface SlideTemplatesSelectionState {
  readonly count: number;
  readonly canInsert: boolean;
  readonly isInserting: boolean;
}

export interface SlideTemplatesPanelHandle {
  insertSelected: () => Promise<void>;
}

interface SlideTemplatesPanelProps {
  readonly onSelectionStateChange?: (state: SlideTemplatesSelectionState) => void;
  /**
   * Where the manifest comes from and how a selection is committed — see
   * hooks/useSlideTemplates.ts. Omit it for the in-pane Asset Library (fetch +
   * insert locally); the Office Dialog window passes the hand-off/messageParent
   * pair instead, since it has neither a bearer token nor PowerPoint.run().
   */
  readonly templatesOptions?: UseSlideTemplatesOptions;
}

const PREVIEW_ONLY_HINT = "This template can be previewed but not inserted yet";

function matchesSearch(template: SlideTemplate, query: string): boolean {
  if (!query) return true;
  const haystack = `${template.name} ${template.category ?? ""} ${template.layoutClass ?? ""}`.toLowerCase();
  return haystack.includes(query.toLowerCase());
}

/** Per-role class, so the wireframe reads as a layout and not a grey mosaic. */
const ROLE_CLASS: Readonly<Record<SlidePreviewRole, string | undefined>> = {
  title: styles.blockTitle,
  body: styles.blockBody,
  image: styles.blockImage,
  chart: styles.blockChart,
  table: styles.blockTable,
  other: styles.blockOther,
};

/**
 * The placeholder rectangles, drawn as a wireframe.
 *
 * The viewBox is a flat 0–100 square with `preserveAspectRatio="none"`: blocks
 * are already percentages of their own axis, so letting each axis stretch
 * independently to the (correctly-proportioned) box is what puts them in the
 * right place. Only the 1-unit corner radius distorts, which is invisible at
 * thumbnail size.
 */
function LayoutWireframe({ preview }: Readonly<{ preview: SlidePreview }>) {
  return (
    <svg className={styles.wireframe} viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      {preview.blocks.map((block, index) => (
        <rect
          // Blocks have no ids of their own; the rect's own geometry is what
          // makes it unique within a card (identical ones are deduped upstream).
          key={`${block.role}-${block.x}-${block.y}-${block.w}-${block.h}-${index}`}
          className={ROLE_CLASS[block.role] ?? ROLE_CLASS.other}
          x={block.x}
          y={block.y}
          width={block.w}
          height={block.h}
          rx={1}
        />
      ))}
    </svg>
  );
}

/**
 * Manifest thumbnail, wireframe, or a bare document-icon placeholder — in
 * that priority order. `hasPhoto` suppresses the icon once a real photo is
 * about to be layered on top (see TemplateThumbnail): a card with nothing
 * left to draw behind a photo that already fills it shouldn't show one.
 */
function ThumbnailBaseLayer({
  template,
  preview,
  hasPhoto,
}: Readonly<{ template: SlideTemplate; preview: SlidePreview | null; hasPhoto: boolean }>) {
  if (template.thumbnailUrl) {
    return <img className={styles.thumbnailImg} src={template.thumbnailUrl} alt="" loading="lazy" />;
  }
  if (preview) {
    return <LayoutWireframe preview={preview} />;
  }
  if (hasPhoto) {
    return null;
  }
  return (
    <div className={styles.thumbnailFallback} aria-hidden="true">
      <span>🗎</span>
    </div>
  );
}

/**
 * Base layer (manifest thumbnail, wireframe, or a bare placeholder) plus, once
 * the card scrolls into view, the slide's own real embedded photo(s) laid on
 * top — see hooks/useSlideTemplateThumbnail.ts for why this is lazy and
 * ../shared/services/pptxSlideImageExtraction.ts for why it isn't sourced from the manifest.
 */
function TemplateThumbnail({
  template,
  loadThumbnail,
}: Readonly<{
  template: SlideTemplate;
  loadThumbnail?: (template: SlideTemplate) => Promise<readonly ExtractedSlideImage[]>;
}>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const images = useSlideTemplateThumbnail(template, rootRef, loadThumbnail);

  // Once a card has its real photo(s), the wireframe's "image"-role rects
  // are just a stale placeholder for the same region — drawing both reads as
  // a jumbled mix of real content and abstract blocks, not a clean preview.
  const wireframePreview = useMemo(() => {
    if (images.length === 0 || !template.preview) return template.preview;
    const blocks = template.preview.blocks.filter((block) => block.role !== "image");
    return blocks.length > 0 ? { ...template.preview, blocks } : null;
  }, [images.length, template.preview]);

  return (
    <div ref={rootRef} className={styles.thumbnailStack}>
      <ThumbnailBaseLayer template={template} preview={wireframePreview} hasPhoto={images.length > 0} />
      {images.map((image, index) => (
        <img
          // Extracted fresh per render from the slide's own picture list —
          // there's no stable id, and position + source together are unique.
          key={`${image.xPct}-${image.yPct}-${index}`}
          className={styles.thumbnailPhoto}
          src={image.dataUrl}
          alt=""
          style={{ left: `${image.xPct}%`, top: `${image.yPct}%`, width: `${image.wPct}%`, height: `${image.hPct}%` }}
        />
      ))}
    </div>
  );
}

function TemplateCard({
  template,
  selected,
  showPreviewOnlyBadge,
  onToggle,
  loadThumbnail,
}: Readonly<{
  template: SlideTemplate;
  selected: boolean;
  showPreviewOnlyBadge: boolean;
  onToggle: (id: string) => void;
  loadThumbnail?: (template: SlideTemplate) => Promise<readonly ExtractedSlideImage[]>;
}>) {
  const toggle = () => onToggle(template.id);

  return (
    <button
      type="button"
      className={selected ? `${styles.card} ${styles.cardSelected}` : styles.card}
      aria-pressed={selected}
      title={template.description ?? template.name}
      onClick={toggle}
    >
      <div className={styles.thumbnail}>
        <div className={styles.thumbnailInner}>
          <TemplateThumbnail template={template} loadThumbnail={loadThumbnail} />
        </div>
        {selected && (
          <span className={styles.selectedTick} aria-hidden="true">
            ✓
          </span>
        )}
        {/* `insertable`, not `sourceFileUrl`/`slideIndex` — the dialog's copy
            of the manifest has neither field at all (see
            asset-library/services/assetLibraryDialogChannel.ts). */}
        {showPreviewOnlyBadge && (
          <span className={styles.previewOnlyBadge} title={PREVIEW_ONLY_HINT}>
            Preview only
          </span>
        )}
      </div>
      <div className={styles.cardFooter}>
        <span className={styles.cardName}>{template.name}</span>
        {template.category && <span className={styles.categoryChip}>{template.category.toUpperCase()}</span>}
      </div>
    </button>
  );
}

interface CategoryCount {
  readonly label: string;
  readonly count: number;
}

/**
 * "FILTER BY" sidebar — categories are the manifest's own `category` field
 * (e.g. "Bio", "Chart"), the same value each card's chip now shows; there's
 * no separate taxonomy or favourites concept behind this, by design.
 */
function CategoryFilterSidebar({
  categories,
  totalCount,
  activeCategory,
  onSelect,
}: Readonly<{
  categories: readonly CategoryCount[];
  totalCount: number;
  activeCategory: string | null;
  onSelect: (category: string | null) => void;
}>) {
  return (
    <nav className={styles.filterSidebar} aria-label="Filter slide templates by category">
      <div className={styles.filterHeading}>Filter by</div>
      <ul className={styles.filterList}>
        <FilterRow label="All" count={totalCount} active={activeCategory === null} onSelect={() => onSelect(null)} />
        {categories.map((category) => (
          <FilterRow
            key={category.label}
            label={category.label}
            count={category.count}
            active={activeCategory === category.label}
            onSelect={() => onSelect(category.label)}
          />
        ))}
      </ul>
    </nav>
  );
}

function FilterRow({
  label,
  count,
  active,
  onSelect,
}: Readonly<{ label: string; count: number; active: boolean; onSelect: () => void }>) {
  return (
    <li>
      <button
        type="button"
        className={active ? `${styles.filterItem} ${styles.filterItemActive}` : styles.filterItem}
        aria-pressed={active}
        onClick={onSelect}
      >
        <span className={styles.filterLabel}>{label}</span>
        <span className={styles.filterCount}>{count}</span>
      </button>
    </li>
  );
}

export const SlideTemplatesPanel = forwardRef<SlideTemplatesPanelHandle, SlideTemplatesPanelProps>(
  function SlideTemplatesPanel({ onSelectionStateChange, templatesOptions }, ref) {
    const {
      loadState,
      insertStatus,
      templates,
      selectedIds,
      selectedCount,
      toggleSelected,
      retry,
      insertSelected,
      isInserting,
      insertError,
    } = useSlideTemplates(templatesOptions);
    const loadThumbnail = templatesOptions?.loadThumbnail;
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState<string | null>(null);

    // Sidebar options, built from the manifest's own `category` field — the
    // same value each card's chip shows — not a separate curated taxonomy.
    const categoryCounts = useMemo(() => {
      const counts = new Map<string, number>();
      for (const template of templates) {
        if (!template.category) continue;
        counts.set(template.category, (counts.get(template.category) ?? 0) + 1);
      }
      return Array.from(counts, ([label, count]) => ({ label, count })).sort(
        (a, b) => b.count - a.count || a.label.localeCompare(b.label),
      );
    }, [templates]);

    const visibleTemplates = useMemo(
      () =>
        templates.filter(
          (template) => matchesSearch(template, search) && (categoryFilter === null || template.category === categoryFilter),
        ),
      [templates, search, categoryFilter],
    );

    // Every card badged "Preview only" says nothing; one notice above the grid
    // does. Only badge cards when the set is actually mixed.
    const noneInsertable = useMemo(
      () => templates.length > 0 && templates.every((template) => !template.insertable),
      [templates],
    );

    const templateCountLabel = templates.length === 1 ? "template" : "templates";
    const resultCountText =
      visibleTemplates.length === templates.length
        ? `${templates.length} ${templateCountLabel}`
        : `${visibleTemplates.length} of ${templates.length}`;

    useImperativeHandle(ref, () => ({ insertSelected }), [insertSelected]);

    useEffect(() => {
      onSelectionStateChange?.({ count: selectedCount, canInsert: selectedCount > 0 && !isInserting, isInserting });
    }, [onSelectionStateChange, selectedCount, isInserting]);

    if (loadState.status === "loading") {
      return (
        <div className={styles.root}>
          <div className={styles.centered}>
            <span className={styles.spinner} aria-hidden="true" />
            <span>Loading slide templates…</span>
          </div>
        </div>
      );
    }

    if (loadState.status === "error") {
      return (
        <div className={styles.root}>
          <div className={styles.centered}>
            <span className={styles.errorText}>{loadState.message}</span>
            <button type="button" className={styles.retryBtn} onClick={retry}>
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.root}>
        <div className={styles.toolbar}>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search slide templates…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search slide templates"
          />
          <span className={styles.resultCount}>{resultCountText}</span>
        </div>

        {noneInsertable && (
          <div className={styles.previewOnlyNotice}>
            These layouts can be previewed but not inserted yet — the library doesn&apos;t publish slide content.
          </div>
        )}

        {insertError && <div className={styles.insertError}>{insertError}</div>}
        {/* The dialog window is the only UI in the real host, so it reports the
            insert outcome itself rather than relying on a task-pane toast. */}
        {!insertError && insertStatus && (
          <output className={styles.insertStatus}>{insertStatus}</output>
        )}

        <div className={styles.body}>
          {categoryCounts.length > 0 && (
            <CategoryFilterSidebar
              categories={categoryCounts}
              totalCount={templates.length}
              activeCategory={categoryFilter}
              onSelect={setCategoryFilter}
            />
          )}

          <div className={styles.gridArea}>
            {visibleTemplates.length === 0 ? (
              <div className={styles.centered}>
                <span>{templates.length === 0 ? "No slide templates are available yet." : "No templates match your search."}</span>
              </div>
            ) : (
              <div className={styles.grid}>
                {visibleTemplates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    selected={selectedIds.has(template.id)}
                    showPreviewOnlyBadge={!template.insertable && !noneInsertable}
                    onToggle={toggleSelected}
                    loadThumbnail={loadThumbnail}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);
